import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import ReporteDinamico from '../components/ReporteDinamico';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['hsl(188, 72%, 28%)', 'hsl(199, 89%, 48%)', 'hsl(36, 95%, 52%)', 'hsl(158, 64%, 39%)', 'hsl(2, 78%, 50%)'];

const Reportes = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.rol === 'SuperAdmin';
  const [stats, setStats] = useState(null);
  const [vehiculos, setVehiculos] = useState([]);
  const [ofertas, setOfertas] = useState([]);
  const [renta, setRenta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  // Dynamic Report State
  const [dinamicoData, setDinamicoData] = useState(null);
  const [loadingDinamico, setLoadingDinamico] = useState(false);

  const evolutivoMensual = useMemo(() => {
    const meses = {};
    vehiculos.forEach(v => {
      const fechaBase = v.fechaAdjudicacion || v.createdAt;
      if (v.estadoPublicacion === 'Adjudicada' && fechaBase) {
        const date = new Date(fechaBase);
        const mesKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!meses[mesKey]) {
          meses[mesKey] = { mes: mesKey, cantidad: 0, monto: 0 };
        }
        
        meses[mesKey].cantidad += 1;
        meses[mesKey].monto += (v.precioAdjudicado || 0);
      }
    });

    return Object.values(meses).sort((a, b) => a.mes.localeCompare(b.mes)).map(item => {
      const [year, month] = item.mes.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      const mesNombre = date.toLocaleString('es-ES', { month: 'short' });
      return {
        ...item,
        mesFormateado: `${mesNombre.charAt(0).toUpperCase() + mesNombre.slice(1)} ${year}`
      };
    });
  }, [vehiculos]);

  const ofertasPorDia = useMemo(() => {
    if (!ofertas || ofertas.length === 0) return [];

    const dias = {};
    ofertas.forEach(o => {
      if (o.hora) {
        const date = new Date(o.hora);
        const dayKey = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
        const sortKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        
        if (!dias[dayKey]) {
          dias[dayKey] = { day: dayKey, sortKey, offers: 0 };
        }
        dias[dayKey].offers += 1;
      }
    });

    return Object.values(dias)
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
      .slice(-30);
  }, [ofertas]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const requests = [
        axios.get(`${API}/stats`),
        axios.get(`${API}/vehiculos`),
        axios.get(`${API}/ofertas`)
      ];

      if (isSuperAdmin) {
        requests.push(axios.get(`${API}/stats/renta`).catch(() => ({ data: { renta: 0, totalAdjudicado: 0 } })));
      }

      const results = await Promise.all(requests);

      setStats(results[0].data);
      setVehiculos(results[1].data);
      setOfertas(results[2].data);

      if (isSuperAdmin && results[3]) {
        setRenta(results[3].data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDinamicoData = async () => {
    setLoadingDinamico(true);
    try {
      const params = new URLSearchParams();
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);

      const response = await axios.get(`${API}/reportes/dinamico?${params.toString()}`);
      setDinamicoData(response.data);
    } catch (error) {
      console.error("Error loading dynamic report data:", error);
      toast.error('Error al cargar datos del reporte dinámico');
    } finally {
      setLoadingDinamico(false);
    }
  };

  // Distribución y Rendimiento por marca
  const marcasStats = useMemo(() => {
    const stats = {};
    vehiculos.forEach((v) => {
      const marca = v.marca || 'Desconocida';
      if (!stats[marca]) {
        stats[marca] = { nombre: marca, publicados: 0, vendidos: 0 };
      }
      stats[marca].publicados += 1;
      if (v.estadoPublicacion === 'Adjudicada') {
        stats[marca].vendidos += 1;
      }
    });

    return Object.values(stats).map(item => ({
      ...item,
      tasaAdjudicacion: item.publicados > 0 ? ((item.vendidos / item.publicados) * 100).toFixed(1) : 0
    })).sort((a, b) => b.publicados - a.publicados);
  }, [vehiculos]);

  // Top 10 Marcas por volumen (para gráfico de barras)
  const topMarcasBarData = marcasStats.slice(0, 10);

  // Market share de ventas (para gráfico de donut)
  const donutData = useMemo(() => {
    const sortedBySales = [...marcasStats].sort((a, b) => b.vendidos - a.vendidos).filter(m => m.vendidos > 0);
    const topSales = sortedBySales.slice(0, 6);
    const others = sortedBySales.slice(6);
    
    const othersVendidos = others.reduce((sum, item) => sum + item.vendidos, 0);
    
    const data = topSales.map(m => ({ name: m.nombre, value: m.vendidos }));
    if (othersVendidos > 0) {
      data.push({ name: 'Otras', value: othersVendidos });
    }
    return data;
  }, [marcasStats]);

  // Comparación precio base vs adjudicado
  const preciosComparacion = vehiculos
    .filter((v) => v.estadoPublicacion === 'Adjudicada' && v.precioAdjudicado)
    .map((v) => ({
      nombre: `${v.marca} ${v.modelo}`,
      base: v.precioBase || 0,
      adjudicado: v.precioAdjudicado,
    }));

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="text-muted-foreground">Cargando...</div></div>;
  }

  return (
    <div className="space-y-6 bg-background min-h-screen">
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-semibold tracking-tight">Reportes y Analítica</h1>
          <p className="text-muted-foreground mt-1">Visualización de datos y métricas</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Tabs
          defaultValue="general"
          className="space-y-6 bg-background min-h-screen"
          onValueChange={(val) => {
            if (val === 'dinamico' && !dinamicoData && !loadingDinamico) {
              loadDinamicoData();
            }
          }}
        >
          <TabsList>
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="ofertas">Ofertas</TabsTrigger>
            <TabsTrigger value="vehiculos">Vehículos</TabsTrigger>
            <TabsTrigger value="dinamico">Análisis Dinámico</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-6 bg-background min-h-screen">
            <div className="grid gap-6 mb-6">
              {/* KPIs */}
              <Card>
                <CardHeader>
                  <CardTitle>Totales</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                    <div>
                      <div className="text-muted-foreground">Publicaciones</div>
                      <div className="text-2xl font-bold">{stats?.total_publicaciones || 0}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Ofertas</div>
                      <div className="text-2xl font-bold">{stats?.total_ofertas || 0}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Adjudicados</div>
                      <div className="text-2xl font-bold">{stats?.vehiculos_adjudicados || 0}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Tasa Adjudicación</div>
                      <div className="text-2xl font-bold">{stats?.tasa_adjudicacion || 0}%</div>
                    </div>
                    {isSuperAdmin && renta !== null && (
                      <div className="col-span-2 md:col-span-4 pt-2 border-t mt-1">
                        <div className="flex items-center justify-between">
                          <div className="text-muted-foreground font-medium">Renta Generada (0.5%)</div>
                          <div className="text-xl font-extrabold text-primary">
                            ${renta.renta.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              {/* Evolutivo Ventas */}
              <Card>
                <CardHeader>
                  <CardTitle>Evolutivo de Ventas (USD)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={evolutivoMensual}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="mesFormateado" />
                        <YAxis tickFormatter={(val) => `$${val.toLocaleString()}`} />
                        <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Monto Adjudicado']} />
                        <Bar dataKey="monto" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Evolutivo Cantidad */}
              <Card>
                <CardHeader>
                  <CardTitle>Evolutivo de Adjudicaciones</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={evolutivoMensual}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="mesFormateado" />
                        <YAxis />
                        <Tooltip formatter={(value) => [value, 'Vehículos Adjudicados']} />
                        <Line type="monotone" dataKey="cantidad" stroke="#10b981" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="ofertas" className="space-y-6 bg-background min-h-screen">
            <Card>
              <CardHeader>
                <CardTitle>Ofertas por Día</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80" data-testid="chart-offers-by-day">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={ofertasPorDia}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="offers" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {preciosComparacion.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Precio Base vs Adjudicado</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={preciosComparacion}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="nombre" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="base" fill="hsl(var(--muted-foreground))" name="Precio Base" />
                        <Bar dataKey="adjudicado" fill="hsl(var(--primary))" name="Precio Adjudicado" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="vehiculos" className="space-y-6 bg-background min-h-screen">
            <div className="grid gap-6 md:grid-cols-2">
              {topMarcasBarData.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Rendimiento por Marca (Top 10)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80" data-testid="chart-brand-bars">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={topMarcasBarData} layout="vertical" margin={{ left: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                          <XAxis type="number" />
                          <YAxis dataKey="nombre" type="category" width={80} tick={{ fontSize: 12 }} />
                          <Tooltip 
                            content={({ active, payload, label }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
                                    <p className="font-bold mb-1 text-foreground">{data.nombre}</p>
                                    <p className="text-muted-foreground">Publicados: {data.publicados}</p>
                                    <p className="text-primary font-medium">Vendidos: {data.vendidos}</p>
                                    <p className="mt-1 pt-1 border-t text-foreground">Tasa Adjudicación: <strong>{data.tasaAdjudicacion}%</strong></p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Legend />
                          <Bar dataKey="publicados" fill="hsl(var(--muted-foreground))" name="Publicados" radius={[0, 4, 4, 0]} />
                          <Bar dataKey="vendidos" fill="hsl(var(--primary))" name="Vendidos" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {donutData.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Distribución de Ventas</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80" data-testid="chart-brand-donut">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={donutData}
                            cx="50%"
                            cy="50%"
                            innerRadius={70}
                            outerRadius={100}
                            paddingAngle={2}
                            dataKey="value"
                            nameKey="name"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            labelLine={false}
                          >
                            {donutData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => [value, 'Vehículos Vendidos']} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Resumen de Vehículos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-muted-foreground">Promedio Km</div>
                      <div className="text-xl font-bold">
                        {vehiculos.length > 0
                          ? Math.round(vehiculos.reduce((sum, v) => sum + v.km, 0) / vehiculos.length).toLocaleString()
                          : 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Año Promedio</div>
                      <div className="text-xl font-bold">
                        {vehiculos.length > 0
                          ? Math.round(vehiculos.reduce((sum, v) => sum + v.anio, 0) / vehiculos.length)
                          : 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Precio Promedio</div>
                      <div className="text-xl font-bold">
                        ${(() => {
                          const vehiculosConPrecio = vehiculos.filter(v => v.precioBase != null && v.precioBase > 0);
                          return vehiculosConPrecio.length > 0
                            ? Math.round(vehiculosConPrecio.reduce((sum, v) => sum + v.precioBase, 0) / vehiculosConPrecio.length).toLocaleString('en-US')
                            : 0;
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="dinamico" className="space-y-6 bg-background min-h-screen">
            <Card>
              <CardHeader>
                <CardTitle>Reportes Dinámicos</CardTitle>
                <div className="flex gap-4 items-end mt-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Fecha Desde</label>
                    <input
                      type="date"
                      className="border rounded-md px-3 py-2 text-sm bg-background text-foreground"
                      value={fechaDesde}
                      onChange={(e) => setFechaDesde(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Fecha Hasta</label>
                    <input
                      type="date"
                      className="border rounded-md px-3 py-2 text-sm bg-background text-foreground"
                      value={fechaHasta}
                      onChange={(e) => setFechaHasta(e.target.value)}
                    />
                  </div>
                  <button
                    onClick={loadDinamicoData}
                    className="bg-primary text-primary-foreground px-4 py-2 rounded-md font-medium text-sm hover:bg-primary/90 transition-colors"
                  >
                    Generar Reporte
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingDinamico ? (
                  <div className="flex items-center justify-center min-h-[600px]">
                    <div className="text-muted-foreground">Cargando datos...</div>
                  </div>
                ) : dinamicoData ? (
                  <ReporteDinamico data={dinamicoData} />
                ) : (
                  <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-muted-foreground">Seleccione fechas y haga clic en Generar Reporte</div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Reportes;